import { useCallback } from 'react'

import { entityHref, type EntityRef } from '@/config/entityRoutes'
import { resolveTabLocation } from '@/config/routeManifest'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { useAppStore } from '@/stores/app'
import { useTabReload } from '@/stores/tabReload'

/**
 * Navigate to a call, duty, or session by reference.
 *
 * `toTask` carries only the shell keys, which is what this needs: a table
 * filter left over from the page you were on can exclude the very row you
 * just asked for.
 *
 * Returns a stable callback so it can be handed to a memoized child.
 */
export function useEntityNavigate(): (ref: EntityRef) => void {
  const { toTask } = useAppNavigate()

  return useCallback(
    (ref: EntityRef) => {
      const href = entityHref(ref)
      const [pathname = '', entitySearch = ''] = href.split('?')

      // Entities addressed by query param (the call sheet) all share one tab,
      // so that tab may already be mounted — and `CallLogs` reads
      // `details`/`call_id` only when it mounts, inside a `useState`
      // initialiser. Bumping the reload nonce remounts the subtree so it
      // re-reads the URL. Path-addressed entities (a duty) open their own tab
      // and mount fresh, so they never need this.
      const tabId = resolveTabLocation({ pathname, search: '', hash: '' })?.id
      const alreadyOpen =
        tabId != null &&
        useAppStore.getState().tabs.items.some((tab) => tab.id === tabId)

      toTask(href)

      if (entitySearch && tabId && alreadyOpen) {
        useTabReload.getState().reload(tabId)
      }
    },
    [toTask]
  )
}
